import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDebouncedPastScrollThreshold } from 'hooks/useDebouncedWindowScroll';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FILTER_MENU_Z_INDEX, layers, useWindowSize } from 'app/spacing';
import SelectOverlay from 'components/Inputs/SelectOverlay';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import useModal from 'components/Modals/useModal';
import { StyledSelectMenu } from '__pages/Common/StyledSelect';
import Icon from 'components/Kizen/Icon';
import BackButton from 'components/Button/BackButton';
import {
  centerNameBreak,
  ToolbarWrapper,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  SaveButton,
  DesktopContent,
  DesktopContactContent,
  DesktopOnlyButton,
  DesktopOnlyButtonWithDropdown,
  MobileContent,
  Header,
  NameWrapper,
  Name,
  ActionsWrapper,
  ThreeDots,
  FlyingDot,
} from 'pages/Common/styles/ControlBar';
import ClientService from 'services/ClientService';
import ArchiveModal from 'components/Modals/ArchiveModal';
import {
  MissingContactInformationModal,
  CONTACT_TYPE_PHONE,
  CONTACT_TYPE_EMAIL,
} from './MissingContactInformationModal';
import ActionBar from './ActionBar';
import styled from '@emotion/styled';
import { Typography } from '@kizen/kds/Typography';
import { ViewSelect } from './styles';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import MobileActivitySelector from 'pages/Common/MobileActivitySelector';
import MobileAutomationSelector from 'pages/Common/MobileAutomationSelector';
import { checkSendEmailAvailability } from 'pages/Common/singleactions/StepContent';

const DEFAULT_NAME = 'Contact';
const NAME_BREAKPOINT = centerNameBreak;

const StyledToolbarWrapper = styled(ToolbarWrapper)`
  z-index: ${FILTER_MENU_Z_INDEX +
  1}; // high z-index to be above the filter dropdowns on the constrained timeline
`;

export const ControlBar = ({
  entityId,
  access = { edit: false, remove: false },
  fullName,
  email,
  emailStatus,
  businessPhone,
  homePhone,
  mobilePhone,
  onSubmit,
  onChange,
  onStartAutomation,
  openPerformModal,
  customObjectId,
  layoutOptions,
  selectedLayout,
  setSelectedLayout,
  hideActions = false,
  canStartAutomations,
  performActionOptions,
  actionsDisabled = false,
  ...props
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const [missingContactType, setMissingContactType] = useState('');
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [showSelectedAutomation, setShowSelectedAutomation] = useState();
  const [missingInfoModalProps, , missingInfoModal] = useModal({
    closeOnSubmit: true,
  });

  const { canSendEmail, notPermittedText, unsubscribed } =
    checkSendEmailAvailability({ access, email, emailStatus, t });

  const sendEmail = useCallback(() => {
    const a = document.createElement('a');
    a.href = `mailto:${email}`;
    a.click();
  }, [email]);

  const sendEmailUnsubscribed = () => {
    sendEmail();
    missingInfoModal.hide();
  };

  const onClickEmail = useCallback(() => {
    if (!canSendEmail || unsubscribed) {
      setMissingContactType(CONTACT_TYPE_EMAIL);
      missingInfoModal.show();
    } else {
      sendEmail();
    }
  }, [canSendEmail, unsubscribed, missingInfoModal, sendEmail]);

  const onClickPhone = useCallback(() => {
    const phoneNumber = businessPhone || mobilePhone || homePhone || null;
    if (!phoneNumber) {
      setMissingContactType(CONTACT_TYPE_PHONE);
      missingInfoModal.show();
    } else {
      const a = document.createElement('a');
      a.href = `tel:${phoneNumber}`;
      a.click();
    }
  }, [businessPhone, homePhone, mobilePhone, missingInfoModal]);

  const actionOptions = useMemo(
    () =>
      [
        {
          value: 'phone',
          label: t('Call'),
          onClick: onClickPhone,
        },
        {
          value: 'email',
          label: t('Send Email'),
          onClick: onClickEmail,
        },
        {
          value: 'activity',
          label: t('Log Activity'),
          onClick: () => setShowLogActivity(true),
        },
        canStartAutomations && {
          value: 'automations',
          label: t('Start Agentic Workflow'),
          onClick: () => setShowSelectedAutomation(true),
        },
      ].filter(Boolean),
    [t, canStartAutomations, onClickPhone, onClickEmail]
  );

  const history = useHistory();
  const scrolled = useDebouncedPastScrollThreshold();

  const [tooltipProps, tooltip] = useTruncationTooltip();
  const { width: screenWidth } = useWindowSize();

  const [showActionMenu, setShowActionMenu] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const actionButtonRef = useRef(null);

  const titleRef = useRef(null);

  useEffect(
    () => {
      if (!scrolled) {
        // makes sure the menus close if scrolled
        setShowActionMenu(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrolled]
  );

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setSubmitting(false);
    }
  };

  const [confirmDeletionModalProps, confirmDeletionModalTriggerProps] =
    useModal();

  const handleArchiveContact = useCallback(async () => {
    await ClientService.archive(entityId);
    history.push('/clients');
  }, [entityId, history]);

  return (
    <>
      <StyledToolbarWrapper
        className="toolbar-wrapper"
        scrolled={scrolled}
        {...props}
      >
        <Toolbar className="toolbar" scrolled={scrolled}>
          <ToolbarLeft nameBreakpoint={NAME_BREAKPOINT} style={{ gap: '15px' }}>
            <BackButton to="/clients">
              <DesktopContent as="span">{t('back')}</DesktopContent>
            </BackButton>
            {layoutOptions?.length > 1 ? (
              <div className="kds flex items-center gap-x-spacer-10 ml-[-20px]">
                <Typography>{t('View')}</Typography>
                <ViewSelect
                  options={layoutOptions}
                  onChange={setSelectedLayout}
                  value={selectedLayout}
                />
              </div>
            ) : null}
          </ToolbarLeft>
          <DesktopContactContent nameBreakpoint={NAME_BREAKPOINT}>
            <NameWrapper>
              <Name {...tooltipProps} type="header" data-qa="contact-full-name">
                {fullName || DEFAULT_NAME}
              </Name>
            </NameWrapper>
          </DesktopContactContent>
          <MobileContent>
            <Header ref={titleRef} {...tooltipProps} type="subheader">
              {fullName || DEFAULT_NAME}
            </Header>
          </MobileContent>
          {!hideActions ? (
            <ToolbarRight
              nameBreakpoint={NAME_BREAKPOINT}
              style={{ alignItems: 'center', gap: '15px' }}
            >
              {access.remove && (
                <DesktopOnlyButton
                  {...confirmDeletionModalTriggerProps}
                  color="red"
                >
                  {t('Archive')}
                </DesktopOnlyButton>
              )}
              {performActionOptions?.length ? (
                <DesktopOnlyButtonWithDropdown
                  noSpace
                  color="blue"
                  onChange={onChange}
                  onClick={openPerformModal}
                  options={performActionOptions}
                  data-qa="perform-action-button"
                  disabled={actionsDisabled}
                >
                  {t('Perform Action')}
                </DesktopOnlyButtonWithDropdown>
              ) : null}
              {access.edit && (
                <DesktopOnlyButton
                  disabled={submitting}
                  onClick={handleSave}
                  color="green"
                >
                  {t('Save')}
                </DesktopOnlyButton>
              )}
              <MobileContent>
                <ThreeDots
                  className={`three-dots ${scrolled ? 'is-scrolled' : ''}`}
                  disabled={!scrolled}
                  screenWidth={screenWidth}
                  ref={actionButtonRef}
                  onClick={() => scrolled && setShowActionMenu(true)}
                >
                  <Icon icon="three-dot" />
                  {actionOptions.length > 2 ? (
                    <FlyingDot screenWidth={screenWidth} className="a" />
                  ) : null}
                  <FlyingDot screenWidth={screenWidth} className="b" />
                  <FlyingDot screenWidth={screenWidth} className="c" />
                  {actionOptions.length > 3 ? (
                    <FlyingDot screenWidth={screenWidth} className="d" />
                  ) : null}
                </ThreeDots>
                <SelectOverlay
                  show={showActionMenu}
                  target={titleRef.current}
                  onHide={() => {
                    setShowActionMenu(false);
                  }}
                  zIndexNudge={layers.content(0, 3)}
                >
                  <StyledSelectMenu
                    value={selectedAction || null}
                    onChange={(value) => {
                      setSelectedAction(null);
                      setShowActionMenu(false);
                      value.onClick();
                    }}
                    options={actionOptions}
                    autoFocus={false}
                    placeholder={t('Select Option')}
                    menuOnly
                  />
                </SelectOverlay>
                <MobileActivitySelector
                  showLogActivity={showLogActivity}
                  target={titleRef.current}
                  onSetVisible={(val) => setShowLogActivity(val)}
                  scrolled={scrolled}
                  shownScrolled
                />
                <MobileAutomationSelector
                  showSelectedAutomation={showSelectedAutomation}
                  target={titleRef.current}
                  onSetVisible={(val) => setShowSelectedAutomation(val)}
                  scrolled={scrolled}
                  shownScrolled
                  onStartAutomation={onStartAutomation}
                  customObjectId={customObjectId}
                />
              </MobileContent>
              <MobileContent>
                {access.edit && (
                  <SaveButton
                    variant="text"
                    disabled={submitting}
                    onClick={handleSave}
                  >
                    {t('save')}
                  </SaveButton>
                )}
              </MobileContent>
            </ToolbarRight>
          ) : (
            <ToolbarRight nameBreakpoint={NAME_BREAKPOINT} />
          )}
        </Toolbar>
      </StyledToolbarWrapper>
      <MobileContent>
        <ActionsWrapper className="ActionsWrapper" scrolled={false}>
          <ActionBar
            condensed={false}
            hasEditAccess={access.edit}
            customObjectId={customObjectId}
            scrolled={scrolled}
            onStartAutomation={onStartAutomation}
            canStartAutomations={canStartAutomations}
            onClickEmail={onClickEmail}
            onClickPhone={onClickPhone}
          />
        </ActionsWrapper>
      </MobileContent>
      <ArchiveModal
        {...confirmDeletionModalProps}
        onConfirm={handleArchiveContact}
      >
        {t(
          'This will archive the Contact and all associated information, so that it can no longer be accessed.'
        )}
      </ArchiveModal>
      {missingContactType === CONTACT_TYPE_EMAIL ? (
        <ConfirmationModal
          actionBtnColor={unsubscribed ? 'green' : 'blue'}
          {...missingInfoModalProps}
          buttonText={unsubscribed ? t('Continue') : t('Close')}
          heading={
            unsubscribed
              ? t('Please Confirm This Action')
              : t('Cannot Perform This Action')
          }
          leftBtn={unsubscribed ? undefined : null}
          onConfirm={
            unsubscribed ? sendEmailUnsubscribed : missingInfoModalProps.onHide
          }
        >
          {notPermittedText}
        </ConfirmationModal>
      ) : null}
      {missingContactType === CONTACT_TYPE_PHONE ? (
        <MissingContactInformationModal
          missingContactType={missingContactType}
          {...missingInfoModalProps}
        />
      ) : null}

      {tooltip}
    </>
  );
};

export default ControlBar;
